/**
 * Token-bound monograph signal components shared across companion demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cva } from "class-variance-authority";
import { Gauge, Quote } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "../styling/index.js";
import { colorGradientToAlpha, colorWithAlpha } from "./MonographPanelColor.js";
import type {
  MonographMeterBarProps,
  MonographMeterTone,
  MonographPanelTokens,
  MonographPullQuoteProps,
} from "./MonographPanelPrimitiveTypes.js";

const meterClass = cva("csk-monograph-meter", {
  variants: {
    tone: {
      danger: "csk-monograph-meter--danger",
      info: "csk-monograph-meter--info",
      neutral: "csk-monograph-meter--neutral",
      safe: "csk-monograph-meter--safe",
      warn: "csk-monograph-meter--warn",
    },
  },
  defaultVariants: { tone: "info" },
});

interface SignalFactoryInput {
  readonly tokens: MonographPanelTokens;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function meterPercent(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  return ((clamp(value, min, max) - min) / (max - min)) * 100;
}

function meterColor(tokens: MonographPanelTokens, tone: MonographMeterTone): string {
  if (tone === "safe") return tokens.accentGreen ?? tokens.accentSafe ?? "#22c55e";
  if (tone === "warn") return "#f59e0b";
  if (tone === "danger") return tokens.accentRed ?? tokens.accentForbidden ?? "#ef4444";
  if (tone === "neutral") return tokens.muted;
  return tokens.accentBlue ?? tokens.accent;
}

function ariaText(valueLabel: React.ReactNode): string | undefined {
  if (typeof valueLabel === "string" || typeof valueLabel === "number") {
    return String(valueLabel);
  }
  return undefined;
}

export function createMonographPanelSignals({ tokens }: SignalFactoryInput): {
  readonly MeterBar: (props: MonographMeterBarProps) => React.JSX.Element;
  readonly PullQuote: (props: MonographPullQuoteProps) => React.JSX.Element;
} {
  function PullQuote({
    children,
    source,
    color = tokens.accent,
    className,
  }: MonographPullQuoteProps): React.JSX.Element {
    const prefersReducedMotion = useReducedMotion() ?? false;
    const motionProps = prefersReducedMotion
      ? { initial: false as const }
      : {
          animate: { opacity: 1, y: 0 },
          initial: { opacity: 0, y: 6 },
          transition: { duration: 0.18 },
        };

    return (
      <motion.blockquote
        className={cn("csk-monograph-pull-quote", className)}
        {...motionProps}
        style={{
          alignItems: "flex-start",
          background: colorWithAlpha(color, "0f"),
          border: `1px solid ${colorWithAlpha(color, "35")}`,
          borderLeft: `3px solid ${color}`,
          borderRadius: "0 12px 12px 0",
          display: "flex",
          gap: 14,
          margin: 0,
          padding: "16px 20px",
        }}
      >
        <Quote aria-hidden="true" color={color} size={19} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: tokens.bright,
              fontSize: 15.5,
              fontStyle: "italic",
              lineHeight: 1.75,
            }}
          >
            {children}
          </div>
          {source ? (
            <footer
              style={{
                color: tokens.muted,
                fontSize: 12,
                fontStyle: "normal",
                letterSpacing: 0,
                marginTop: 10,
              }}
            >
              {source}
            </footer>
          ) : null}
        </div>
      </motion.blockquote>
    );
  }

  function MeterBar({
    label,
    value,
    min = 0,
    max = 100,
    tone = "info",
    color = meterColor(tokens, tone),
    valueLabel,
    description,
    ariaLabel,
    className,
  }: MonographMeterBarProps): React.JSX.Element {
    const prefersReducedMotion = useReducedMotion() ?? false;
    const boundedValue = clamp(value, min, max);
    const percent = meterPercent(value, min, max);
    const width = `${percent.toFixed(1)}%`;
    const motionProps = prefersReducedMotion
      ? { initial: false as const }
      : { animate: { width }, initial: { width: "0%" }, transition: { duration: 0.25 } };

    return (
      <div
        className={cn(meterClass({ tone }), className)}
        style={{ display: "flex", flexDirection: "column", gap: 7, width: "100%" }}
      >
        <div
          style={{
            alignItems: "center",
            display: "flex",
            gap: 10,
            justifyContent: "space-between",
          }}
        >
          <span
            style={{
              alignItems: "center",
              color: tokens.muted,
              display: "inline-flex",
              fontFamily: tokens.mono,
              fontSize: 11,
              fontWeight: 700,
              gap: 6,
              letterSpacing: 0,
              textTransform: "uppercase",
            }}
          >
            <Gauge aria-hidden="true" color={color} size={14} />
            {label}
          </span>
          {valueLabel ? (
            <span style={{ color: tokens.bright, fontFamily: tokens.mono, fontSize: 12 }}>
              {valueLabel}
            </span>
          ) : null}
        </div>
        <div
          role="meter"
          aria-label={ariaLabel ?? (typeof label === "string" ? label : "Meter")}
          aria-valuemax={max}
          aria-valuemin={min}
          aria-valuenow={boundedValue}
          aria-valuetext={ariaText(valueLabel)}
          style={{
            background: "rgba(7,6,13,0.72)",
            border: `1px solid ${tokens.border}`,
            borderRadius: 999,
            height: 10,
            overflow: "hidden",
            position: "relative",
            width: "100%",
          }}
        >
          <motion.div
            {...motionProps}
            style={{
              background: colorGradientToAlpha(color, "cc"),
              boxShadow: `0 0 14px ${colorWithAlpha(color, "66")}`,
              height: "100%",
              width,
            }}
          />
        </div>
        {description ? (
          <div style={{ color: tokens.muted, fontFamily: tokens.mono, fontSize: 11 }}>
            {description}
          </div>
        ) : null}
      </div>
    );
  }

  return { MeterBar, PullQuote };
}
